import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { TournamentSummary } from "../../shared/tournament";
import { apiFor, type TournamentApi } from "./api";

/**
 * WHICH TOURNAMENT a surface is rendered for (V2-ROADMAP.md Phase 2).
 *
 * The `/t/$slug` layout route resolves the slug against the registry once and
 * provides the result here; every page and panel underneath asks
 * `useTournament()` for the id it should address and `useApi()` for the client
 * already bound to it. Nothing under a tournament route ever spells
 * `/api/t/<id>` or `/t/<slug>` by hand: the two helpers below are the only
 * places those prefixes are written.
 *
 * A context, not a module variable, on purpose: `/super` renders a tournament
 * dialog for whichever event its operator picked, inside its own provider,
 * while the rest of the console has none. A global "current tournament" would
 * have to be juggled around that; a value on the tree simply is what it is.
 */
export interface TournamentCtx {
  /** The Durable Object name and the API path segment: `main`, or the slug. */
  id: string;
  /** The page path segment. Equal to `id` except for the legacy event. */
  slug: string;
  meta: TournamentSummary;
  /** `apiFor(id)`, built once per tournament. */
  api: TournamentApi;
}

const Ctx = createContext<TournamentCtx | null>(null);

export function TournamentProvider({
  tournament,
  children,
}: {
  tournament: TournamentSummary;
  children: ReactNode;
}) {
  // One client per tournament for the life of the provider: components put
  // `api` in effect dependency lists, and a fresh object per render would
  // restart every poll.
  const value = useMemo<TournamentCtx>(
    () => ({
      id: tournament.id,
      slug: tournament.slug,
      meta: tournament,
      api: apiFor(tournament.id),
    }),
    [tournament],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Thrown rather than defaulted: a tournament surface rendered outside a
 *  tournament route is a routing bug, and a silent `main` would quietly read
 *  and write the wrong event. */
export function useTournament(): TournamentCtx {
  const ctx = useContext(Ctx);
  if (ctx === null) throw new Error("useTournament outside a tournament route");
  return ctx;
}

export function useApi(): TournamentApi {
  return useTournament().api;
}

/**
 * The tournament if there is one. For the overlay's data hooks ONLY: under
 * the showcase (`/super?view=overlay`) they are mounted without a tournament
 * and never fetch, because the fixtures feed them. Everything else asks
 * `useTournament()` and gets the throw.
 */
export function useOptionalTournament(): TournamentCtx | null {
  return useContext(Ctx);
}

/** `/t/<slug>` and everything under it. One function, so the segment is one
 *  edit if it ever changes. `rest` starts with a slash or is empty. */
export function tournamentHref(slug: string, rest = ""): string {
  return `/t/${encodeURIComponent(slug)}${rest}`;
}

const LAST_KEY = "score.lastTournament";

/**
 * The tournament this device opened last. A per-device convenience for the
 * front page (an installed board starts at `/` and should be one tap from
 * where it was), and nothing more: `localStorage` can be missing or throw in
 * a private window, and the page renders correctly without it.
 */
export function rememberLastTournament(slug: string): void {
  try {
    localStorage.setItem(LAST_KEY, slug);
  } catch {
    // A private window, or storage disabled: the front page simply has no
    // "last opened" card.
  }
}

export function lastTournament(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}
